// Quote Builder — entry module.
//
// Registers the `quote-builder` Page with the chrome's router. The page is
// hidden from the sidebar (`hidden: true` — launched via CRM / dashboard
// buttons, not the nav). Mount renders a full-screen overlay into the
// chrome's content area: tab strip on the left, quote area on the right,
// Kit-Q chat panel floating, and a bottom action/toolbar strip.
//
// Source-of-truth: kos-qb-v3.js:1286-1318 (`renderWorkspace`), plus the
// component shells in the per-module files.

import { confirmDialog } from "@tkos/shared-ui";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import { getCachedUser } from "../../shell/auth";

import { createStore, MINIMISED_FLAG, setRetailerVatRegistered, defaultVatRate, setRetailerQuoteNoteDefaults } from "./state";
import { mountHeader } from "./header";
import { mountPlansPanel } from "./plans-panel";
import { mountFloatingPlan } from "./floating-plan";
import { mountProductsPanel } from "./products-panel";
import { mountQuoteMeta, mountQuoteItems } from "./quote-area";
import { mountTotals } from "./totals";
import { mountActions } from "./actions";
import { mountSentStamp } from "./sent-stamp";
import { mountToolbar } from "./toolbar";
import { mountKitQ } from "./kit-q";
import { mountPickerArea } from "./picker-area";
import {
  setActivePicker,
  getActivePicker,
  subscribePicker,
  setSelectedCab,
  setSelectedSjbCode,
} from "./picker-selection";
import { quoteTotals } from "./pricing";
import { fmtPence, newId, tempQuoteRef } from "./formatters";
import { fetchRetailerProfile } from "./retailer-profile";
import { showConflictBanner, dismissConflictBanner } from "./conflict-banner";

/** localStorage key for the persisted left-pane width — verbatim
 *  kos-qb-v3.js:1327 (`kos_qb_v3_left_w`). */
const LEFT_WIDTH_KEY = "kos_qb_v3_left_w";

/** Floors for the two columns, and the splitter that sits between them.
 *
 *  The left panel has had a 280px floor since the original port. The right
 *  one had none, so dragging the split wide left the quote column narrower
 *  than a single quote line and it scrolled sideways (Steve, session 3).
 *  Both are mirrored in styles.css — CSS stops a stale persisted width
 *  landing below the floor, this stops a drag reaching it in the first
 *  place. */
const LEFT_MIN_PX = 280;
const RIGHT_MIN_PX = 280;
const SPLITTER_PX = 5;

import "./styles.css";

const quoteBuilderPage: Page = {
  name: "quote-builder",
  label: "Quote Builder",
  icon: "",
  hidden: true,

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    ctx.track({ eventType: "feature_used", feature: "quote-builder", customData: { action: "opened" } });
    // ── Build the overlay shell ─────────────────────────────────────────────
    container.classList.add("qb-page-root");
    container.innerHTML = `
      <div class="qb-shell" role="region" aria-label="Quote Builder">
        <div class="qb-header-slot"></div>
        <div class="qb-body">
          <aside class="qb-left" aria-label="Designs and products">
            <nav class="qb-left__tabs" role="tablist" aria-label="Left panel">
              <button type="button" class="qb-left__tab is-active" data-tab="plans" role="tab" aria-selected="true">Plans</button>
              <button type="button" class="qb-left__tab" data-tab="products" role="tab" aria-selected="false">Products</button>
            </nav>
            <!-- Configure-mode header. Replaces the tab strip while a picker
                 is open: browsing chrome and a configurator stacked together
                 is unusable once the picker has per-leaf rows. -->
            <div class="qb-left__configbar" data-role="left-configbar" hidden>
              <button type="button" class="qb-left__back" data-role="left-back">‹ Back to browsing</button>
            </div>
            <div class="qb-left__pane" data-role="left-pane">
              <!-- Browse and configure are SIBLINGS, both mounted, one shown.
                   Unmounting the browse side on every open would throw away
                   the retailer's query, results and scroll position — and they
                   go back and forth constantly. -->
              <div class="qb-left__browse" data-role="left-browse"></div>
              <div class="qb-left__picker" data-role="picker-area"></div>
            </div>
          </aside>
          <div class="qb-splitter" data-role="splitter" title="Drag to resize"></div>
          <section class="qb-right" aria-label="Quote">
            <div class="qb-right__scroll">
              <div data-role="sent-stamp" hidden></div>
              <div data-role="quote-meta"></div>
              <div data-role="items-area"></div>
              <div class="qb-right__totals" data-role="totals"></div>
              <div class="qb-right__actions" data-role="actions"></div>
              <div class="qb-right__toolbar" data-role="toolbar"></div>
            </div>
          </section>
        </div>
        <div class="qb-kitq-slot" data-role="kitq"></div>
        <!-- Floating surfaces live OUTSIDE .qb-body on purpose: they are
             siblings of the panes, not children, so a pane repaint cannot
             destroy them and the scroll-preserving repaint never sees them
             as an ancestor to restore. -->
        <div data-role="floating-plan"></div>
        <div class="qb-badge" data-role="badge" title="Click to jump to totals" hidden></div>
      </div>
    `;

    // ── Resolve any deep-link params ────────────────────────────────────────
    // The QB can be opened against a specific saved quote from the customer
    // record / quotes list. The server quote id (PG uuid) may arrive either
    // as a router param (`?quoteId=`) or in the legacy hash query. When set,
    // the store loads THAT row on mount instead of the local draft, so a
    // duplicated-then-sent quote (BUG 1) or a sent quote clicked from the
    // full record (BUG 2) opens fully populated rather than empty.
    const hashParams = (() => {
      const h = window.location.hash;
      const i = h.indexOf("?");
      if (i < 0) return new URLSearchParams();
      try { return new URLSearchParams(h.slice(i + 1)); } catch { return new URLSearchParams(); }
    })();
    const navQuoteId = (ctx.params["quoteId"] || hashParams.get("quoteId") || "").trim();
    const navQuoteRef = (ctx.params["quote"] || hashParams.get("quote") || "").trim();
    // Where Close / "return to where you came from" should land. We came
    // from the customer record when a customerId was passed; otherwise fall
    // back to the CRM list.
    const returnCustomerId = (ctx.params["customerId"] || hashParams.get("customerId") || "").trim();

    // ── Bootstrap the store ─────────────────────────────────────────────────
    const store = createStore({
      onSyncError: () => {
        ctx.showToast(
          "Quote saved locally — will retry when connection restored",
          "info",
        );
      },
      // The server refused the save because this quote changed elsewhere. Both
      // copies survive: show the banner and let the user choose. Never silently
      // discard either side (Steve's call, 2026-08-10).
      onConflict: (info) => {
        showConflictBanner({
          ...(info.message ? { message: info.message } : {}),
          onReload: info.reload,
          onKeepEditing: () => {
            ctx.showToast(
              "Keeping your version. It is not saved — reload to get the latest, or change something to retry.",
              "info",
            );
          },
        });
      },
      // The save would have emptied a populated quote without explicit intent.
      // Always surfaced: this is the failure mode that cost KOS-Q-VQCX its lines.
      onSaveBlocked: (info) => {
        ctx.showToast(
          info.message
            || "That save was blocked because it would have removed every line from the quote.",
          "error",
        );
      },
      ...(navQuoteId ? { initialServerId: navQuoteId } : {}),
    });
    // Clear the minimised flag the moment the user re-opens.
    try { localStorage.removeItem(MINIMISED_FLAG); } catch { /* ignore */ }

    // "New quote" means BLANK. Every entry point that promises a new quote
    // passes fresh=1; the store otherwise rehydrates the last working copy from
    // localStorage, so "New quote" reopened whatever was last worked on — most
    // visibly the quote you had just closed.
    //
    // Deliberately an explicit request rather than relying on each exit path to
    // tidy up after itself: Close and Delete do discard their copy, but Minimise
    // must NOT (the dashboard resume banner depends on it) and a plain browser
    // back-out cleans up nothing. Asking for what you want at the entry point is
    // the only version that holds for all of them.
    //
    // Skipped when a specific quote was requested — an explicit quoteId/quote
    // always wins over a stale fresh flag.
    const wantFresh = (ctx.params["fresh"] || hashParams.get("fresh") || "") === "1";
    if (wantFresh && !navQuoteId && !navQuoteRef) {
      store.resetLocal();
    }

    // Reference-only deep link (legacy hash / Next-Action prompt): no server
    // id to hand the bootstrap, so resolve it from the list then load by id.
    // Skipped when a quoteId was supplied (the bootstrap already handles it).
    if (!navQuoteId && navQuoteRef) {
      void (async () => {
        try {
          const res = await fetch("/api/portal/quote-builder", {
            credentials: "include",
            headers: { "x-retailer-id": getCachedUser()?.retailerSlug || getCachedUser()?.retailerId || "" },
          });
          const data = await res.json();
          const list: Array<{ id: string; reference: string }> = Array.isArray(data.quotes) ? data.quotes : [];
          const hit = list.find((q) => q.reference === navQuoteRef);
          if (hit?.id) await store.loadServer(hit.id);
        } catch { /* keep the local draft if lookup fails */ }
      })();
    }

    // Seed the new-quote default VAT rate from the retailer's VAT-registration
    // setting (BUG/FEATURE 8). Only touches a brand-new, untouched quote — a
    // loaded or in-progress quote keeps its own rate, and the per-quote VAT
    // field stays editable. `defaultQuote()` already reads the cached flag for
    // any quote created afterwards (e.g. via Clear).
    void fetchRetailerProfile().then((profile) => {
      if (!profile) return;
      setRetailerVatRegistered(profile.vatRegistered);
      setRetailerQuoteNoteDefaults(profile.quoteIntroDefault ?? "", profile.quoteClosingDefault ?? "");
      if (navQuoteId || navQuoteRef) return; // editing an existing quote
      const q = store.get();
      const fresh = !q.serverId && q.zones.every((z) => z.items.length === 0);
      if (fresh) {
        store.seedVatRate(defaultVatRate());
        store.seedNotes(profile.quoteIntroDefault ?? "", profile.quoteClosingDefault ?? "");
      }
    });

    // When opened from a CRM record without a specific quote —
    // navigate("quote-builder", { customer, customerId }) — assign that
    // customer to the quote so it isn't left unassigned. Fill-only: never
    // clobber a customer already on a resumed draft (the user can "Start
    // fresh" to clear it). (Item 5) Skipped when loading an existing quote,
    // whose own customer must win.
    if (!navQuoteId && !navQuoteRef) {
      const navCustomer = (ctx.params["customer"] || "").trim();
      const navCustomerId = (ctx.params["customerId"] || "").trim();
      if (navCustomer) {
        store.set((d) => {
          if (!d.customer) d.customer = navCustomer;
          if (navCustomerId && !d.customerId) d.customerId = navCustomerId;
        });
      }
    }

    // ── Wire each subsystem ────────────────────────────────────────────────
    const headerHost = container.querySelector<HTMLElement>(".qb-header-slot")!;
    const leftPane = container.querySelector<HTMLElement>("[data-role='left-browse']")!;
    const leftEl0 = container.querySelector<HTMLElement>(".qb-left")!;
    const configBar = container.querySelector<HTMLElement>("[data-role='left-configbar']")!;
    const tabStrip = container.querySelector<HTMLElement>(".qb-left__tabs")!;
    const quoteMetaHost = container.querySelector<HTMLElement>("[data-role='quote-meta']")!;
    const pickerAreaHost = container.querySelector<HTMLElement>("[data-role='picker-area']")!;
    const quoteItemsHost = container.querySelector<HTMLElement>("[data-role='items-area']")!;
    const totalsHost = container.querySelector<HTMLElement>("[data-role='totals']")!;
    const actionsHost = container.querySelector<HTMLElement>("[data-role='actions']")!;
    const toolbarHost = container.querySelector<HTMLElement>("[data-role='toolbar']")!;
    const kitqHost = container.querySelector<HTMLElement>("[data-role='kitq']")!;
    // Reset picker selection on mount.
    setActivePicker(null);

    // Left tab: starts on Plans.
    let activeLeftTab: "plans" | "products" = "plans";
    let cleanupLeft: () => void = () => {};
    const renderLeft = (): void => {
      cleanupLeft();
      leftPane.replaceChildren();
      if (activeLeftTab === "plans") {
        cleanupLeft = mountPlansPanel(leftPane, store, ctx);
      } else {
        cleanupLeft = mountProductsPanel(leftPane, store);
      }
    };
    renderLeft();

    /* ── Browse vs configure ────────────────────────────────────────────
     *
     * The pickers moved out of the right column on 2026-09-05, so the left
     * panel now does two jobs. Stacking them — supplier pills, defaults
     * strip, category chips, search box AND a configurator with a row per
     * door leaf — is not a cluttered panel, it is an unusable one. So the
     * panel has a mode.
     *
     * Both sides stay MOUNTED and visibility is toggled. Unmounting the
     * browse side on every open would discard the query, the results and the
     * scroll position, and a retailer moves between the two constantly:
     * search, configure, add, search again.
     *
     * Driven off picker-selection, which is the existing channel — the panel
     * does not need to know who opened a picker, only that one is open.
     */
    const syncLeftMode = (): void => {
      const configuring = getActivePicker() !== null;
      leftEl0.classList.toggle("qb-left--configuring", configuring);
      tabStrip.hidden = configuring;
      configBar.hidden = !configuring;
      // A picker opening is a navigation: start it at the top rather than
      // wherever the results list happened to be scrolled to.
      if (configuring) pickerAreaHost.scrollTop = 0;
    };
    const unsubLeftMode = subscribePicker(() => syncLeftMode());
    syncLeftMode();

    container.querySelector<HTMLButtonElement>("[data-role='left-back']")
      ?.addEventListener("click", () => {
        // Clear the selections too, not just the category. A picker is
        // re-opened by its selection channel, so leaving a cab or an SJB code
        // set would spring the configurator straight back open.
        setSelectedCab(null);
        setSelectedSjbCode(null);
        setActivePicker(null);
      });

    container.querySelectorAll<HTMLButtonElement>(".qb-left__tab").forEach((btn) => {
      btn.addEventListener("click", () => {
        activeLeftTab = btn.dataset.tab === "products" ? "products" : "plans";
        container.querySelectorAll<HTMLButtonElement>(".qb-left__tab").forEach((b) => {
          const active = b.dataset.tab === activeLeftTab;
          b.classList.toggle("is-active", active);
          b.setAttribute("aria-selected", active ? "true" : "false");
        });
        renderLeft();
      });
    });

    // Kit-Q first (so the header can poke it open).
    const kitq = mountKitQ(kitqHost, store, ctx);

    const cleanupHeader = mountHeader(headerHost, store, {
      onMinimise: () => {
        try { localStorage.setItem(MINIMISED_FLAG, "1"); } catch { /* ignore */ }
        ctx.navigate("dashboard");
      },
      onClear: () => {
        void (async () => {
          if (!(await confirmDialog(
            "Clear this quote and start a new one? Anything not saved server-side will be lost.",
            { title: "Clear quote", confirmLabel: "Clear quote", destructive: true },
          ))) return;
          // resetLocal, NOT replace(defaultQuote()). replace() schedules a
          // server sync, and a blank quote with no serverId POSTs as a brand-new
          // row — so every press of Clear left an empty draft behind in the
          // database. Clear is a local action: it abandons the working copy and
          // gives you a blank one. The quote you cleared is already saved and
          // stays reachable from Quotes.
          store.resetLocal();
          ctx.showToast("Started a fresh quote", "info");
        })();
      },
      onSave: () => {
        // Autosave already keeps storage in sync. We still toast for feedback.
        ctx.showToast("Saved", "success");
      },
      onClose: () => {
        // Return to wherever the QB was opened from — the customer record
        // when a customerId came through, else the CRM list.
        const goBack = (): void => {
          // Forget the local working copy. Without this the closed quote stayed
          // in localStorage and the next "New quote" — which navigates here with
          // no params and therefore resumes the snapshot — reopened the quote
          // just closed instead of starting blank.
          store.resetLocal();
          if (returnCustomerId) ctx.navigate("customer", { id: returnCustomerId });
          else ctx.navigate("crm");
        };
        // No unsaved work → close immediately. Otherwise confirm so a
        // pending (un-synced) edit isn't silently dropped.
        if (!store.isDirty()) { goBack(); return; }
        void (async () => {
          if (await confirmDialog(
            "You have unsaved changes. Close anyway?",
            { title: "Unsaved changes", confirmLabel: "Close anyway", destructive: true },
          )) goBack();
        })();
      },
      onOpenCustomerRecord: (customerId) => {
        // Deliberately does NOT discard the local copy: this is a hop over to
        // the record, not "I am finished with this quote". The working copy
        // stays resumable, and "New quote" is blank regardless because it asks
        // for a fresh quote explicitly (see the `fresh` param on mount).
        ctx.navigate("customer", { id: customerId });
      },
      onKitQ: () => kitq.open(),
      onToggleLeft: () => container.classList.toggle("qb-left--collapsed"),
      onShowShortcuts: () => showShortcutsModal(),
      onLoadQuote: () => openQuotesArchive(store, ctx),
      onDuplicate: () => {
        // Deep clone the current quote, then reset it to a fresh DRAFT for the
        // SAME customer: new local + zone/item ids, no server id (so it saves as
        // a new row), a temp reference, draft status, and any sent/approval state
        // cleared. Named "<project> (copy)" (Steve, 2026-10-10 — it was
        // "<project> - Copy", and a bare "Copy" when the project was blank).
        // A blank project falls back to the title the customer is shown for
        // it (actions.ts send: "<customer> Kitchen Quote"), so the copy still
        // says whose quote it is. Opens immediately for editing.
        const q = store.get();
        const dup = JSON.parse(JSON.stringify(q)) as typeof q;
        dup.id = newId("q");
        delete dup.serverId;
        delete dup.lastServerSyncAt;
        delete dup.customerSignature;
        // The customer has never seen THIS quote. Carried over, it painted the
        // original's "Opened <date>" pill on the copy until the next reload.
        delete dup.openedAt;
        dup.reference = tempQuoteRef();
        dup.status = "draft";
        const baseName = q.project?.trim()
          || (q.customer?.trim() ? `${q.customer.trim()} Kitchen Quote` : "");
        dup.project = baseName ? `${baseName} (copy)` : "(copy)";
        dup.zones = dup.zones.map((z) => ({
          ...z,
          id: newId("z"),
          items: z.items.map((it) => ({ ...it, id: newId("i") })),
        }));
        dup.lastSavedAt = Date.now();
        dup.createdAt = Date.now();
        store.replace(dup);
        ctx.showToast("Quote duplicated — you're now editing the copy", "success");
      },
    }, ctx);

    const cleanupQuoteMeta = mountQuoteMeta(quoteMetaHost, store, ctx);
    const cleanupPickerArea = mountPickerArea(pickerAreaHost, store);
    const cleanupQuoteItems = mountQuoteItems(quoteItemsHost, store, ctx);
    const cleanupTotals = mountTotals(totalsHost, store, ctx);
    const cleanupActions = mountActions(actionsHost, store, ctx);
    // "Sent" stamp + edited-since-sent warning (quote snapshots, 0098).
    const cleanupSentStamp = mountSentStamp(
      container.querySelector<HTMLElement>("[data-role='sent-stamp']")!, store, ctx);
    const cleanupToolbar = mountToolbar(toolbarHost, store, ctx);
    // Floating plan thumbnail. Renders nothing at all unless the quote has a
    // customer assigned AND that customer has at least one IMAGE uploaded, so
    // it costs an empty div on every other quote.
    const floatingPlanHost = container.querySelector<HTMLElement>("[data-role='floating-plan']");
    const cleanupFloatingPlan = floatingPlanHost
      ? mountFloatingPlan(floatingPlanHost, store)
      : () => {};

    // ── Splitter — drag-to-resize left pane (verbatim source 1319-1370) ────
    const splitter = container.querySelector<HTMLElement>("[data-role='splitter']");
    const leftEl = container.querySelector<HTMLElement>(".qb-left");
    const cleanupSplitter = (splitter && leftEl)
      ? wireSplitter(splitter, leftEl, container)
      : (): void => {};

    // ── Floating grand-total badge (verbatim source 1476-1482) ────────────
    const badgeEl = container.querySelector<HTMLElement>("[data-role='badge']");
    const cleanupBadge = badgeEl ? wireBadge(badgeEl, store, container) : (): void => {};

    // ── Selected-line tracking (verbatim source 5686-5691) ─────────────────
    let selectedLineId: string | null = null;
    const onLineClick = (e: MouseEvent): void => {
      const line = (e.target as HTMLElement | null)?.closest<HTMLElement>(".qb-line");
      if (line && line.dataset["item"]) {
        selectedLineId = line.dataset["item"]!;
        container.querySelectorAll(".qb-line.is-selected").forEach((el) => el.classList.remove("is-selected"));
        line.classList.add("is-selected");
      }
    };
    container.addEventListener("click", onLineClick);

    // ── Keyboard shortcuts (verbatim source 5628-5683) ────────────────────
    const cleanupShortcuts = wireShortcuts({
      onSave: () => ctx.showToast("Saved", "success"),
      onMinimise: () => {
        try { localStorage.setItem(MINIMISED_FLAG, "1"); } catch { /* ignore */ }
        ctx.navigate("dashboard");
      },
      onToggleLeft: () => container.classList.toggle("qb-left--collapsed"),
      onShowShortcuts: () => showShortcutsModal(),
      onOpenPicker: () => {
        // Source line 5666-5670: default to doors picker.
        setActivePicker("doors");
        const slot = container.querySelector<HTMLElement>("[data-role='picker-area']");
        slot?.scrollIntoView({ behavior: "smooth", block: "start" });
      },
      onDuplicateLine: () => {
        // Source 5655-5664: dup selectedLineId, else last item across zones.
        const q = store.get();
        let target = selectedLineId;
        if (!target) {
          for (let i = q.zones.length - 1; i >= 0; i--) {
            const items = q.zones[i]!.items;
            if (items.length > 0) { target = items[items.length - 1]!.id; break; }
          }
        }
        if (!target) return;
        const id = target;
        store.set((d) => {
          for (const z of d.zones) {
            const idx = z.items.findIndex((it) => it.id === id);
            if (idx >= 0) {
              const src = z.items[idx]!;
              const copy = { ...src, id: `i${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}` };
              z.items.splice(idx + 1, 0, copy);
              break;
            }
          }
        });
        ctx.showToast("Duplicated", "success");
      },
      onCustomerPdf: () => ctx.showToast("Customer PDF — coming soon", "info"),
      onInternalPdf: () => ctx.showToast("Internal PDF — coming soon", "info"),
    });

    // ── Optional: read `?customer=...&quote=...` from the URL fragment so
    // CRM "Quote this customer" buttons can deep-link. Convention is to
    // append `?customer=name` to the hash (so it lives in `location.hash`
    // not in the global query string). The router only consumes the
    // leading `#/page` portion; the rest is ours.
    //
    // Skipped entirely when opening an existing quote (by id or reference):
    // the loaded quote owns its customer/project/reference and must not be
    // overwritten by the URL — doing so was a contributor to the "opens
    // empty / wrong reference" symptom (BUG 1 & 2).
    if (!navQuoteId && !navQuoteRef) {
      const c = hashParams.get("customer");
      const p = hashParams.get("project");
      const r = hashParams.get("quote");
      if (c || p || r) {
        store.set((d) => {
          if (c) d.customer = c;
          if (p) d.project = p;
          if (r) d.reference = r;
        });
      }
    }

    // ── Cleanup ─────────────────────────────────────────────────────────────
    return (): void => {
      // The banner is appended to document.body, so it would outlive the page
      // and dangle a reload closure over a destroyed store.
      dismissConflictBanner();
      cleanupHeader();
      cleanupLeft();
      cleanupQuoteMeta();
      cleanupPickerArea();
      cleanupQuoteItems();
      cleanupTotals();
      cleanupActions();
      cleanupSentStamp();
      cleanupToolbar();
      cleanupFloatingPlan();
      cleanupSplitter();
      unsubLeftMode();
      cleanupBadge();
      cleanupShortcuts();
      container.removeEventListener("click", onLineClick);
      kitq.destroy();
      store.destroy();
      setActivePicker(null);
      container.classList.remove("qb-page-root");
    };
  },
};

/** Splitter drag handler — verbatim port of source `wireSplitter`
 *  (kos-qb-v3.js:1319–1370). Min 280px, max 70% of window, mouse + touch. */
function wireSplitter(splitter: HTMLElement, leftEl: HTMLElement, root: HTMLElement): () => void {
  /** The widest the left panel may be right now: never so wide that the quote
   *  column drops below its floor, and never past 70% of the window. On a
   *  narrow screen the floor binds; on a wide one the 70% cap does. */
  const maxLeft = (): number =>
    Math.max(LEFT_MIN_PX,
             Math.min(window.innerWidth * 0.7,
                      window.innerWidth - RIGHT_MIN_PX - SPLITTER_PX));

  // Restore saved width on mount — clamped, because a width persisted before
  // the right-hand floor existed can be wider than is now allowed.
  try {
    const saved = parseFloat(localStorage.getItem(LEFT_WIDTH_KEY) || "");
    if (saved > 200) {
      root.style.setProperty("--qb-left-w",
        Math.max(LEFT_MIN_PX, Math.min(maxLeft(), saved)) + "px");
    }
  } catch { /* ignore */ }

  let dragging = false;
  let startX = 0;
  let startW = 0;

  const onMove = (clientX: number): void => {
    if (!dragging) return;
    const newW = Math.max(LEFT_MIN_PX, Math.min(maxLeft(), startW + (clientX - startX)));
    root.style.setProperty("--qb-left-w", newW + "px");
  };

  const onMouseDown = (e: MouseEvent): void => {
    dragging = true;
    startX = e.clientX;
    startW = leftEl.getBoundingClientRect().width;
    splitter.classList.add("is-dragging");
    document.body.style.userSelect = "none";
    e.preventDefault();
  };
  const onTouchStart = (e: TouchEvent): void => {
    if (!e.touches.length) return;
    dragging = true;
    startX = e.touches[0]!.clientX;
    startW = leftEl.getBoundingClientRect().width;
    splitter.classList.add("is-dragging");
  };
  const onMouseMove = (e: MouseEvent): void => onMove(e.clientX);
  const onTouchMove = (e: TouchEvent): void => {
    if (e.touches.length) onMove(e.touches[0]!.clientX);
  };
  const endDrag = (): void => {
    if (!dragging) return;
    dragging = false;
    splitter.classList.remove("is-dragging");
    document.body.style.userSelect = "";
    try {
      const w = Math.round(leftEl.getBoundingClientRect().width);
      localStorage.setItem(LEFT_WIDTH_KEY, String(w));
    } catch { /* ignore */ }
  };

  splitter.addEventListener("mousedown", onMouseDown);
  splitter.addEventListener("touchstart", onTouchStart, { passive: true });
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("touchmove", onTouchMove, { passive: true });
  document.addEventListener("mouseup", endDrag);
  document.addEventListener("touchend", endDrag);

  return (): void => {
    splitter.removeEventListener("mousedown", onMouseDown);
    splitter.removeEventListener("touchstart", onTouchStart);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("touchmove", onTouchMove);
    document.removeEventListener("mouseup", endDrag);
    document.removeEventListener("touchend", endDrag);
  };
}

interface ShortcutHandlers {
  onSave: () => void;
  onMinimise: () => void;
  onToggleLeft: () => void;
  onShowShortcuts: () => void;
  onOpenPicker: () => void;
  onDuplicateLine: () => void;
  onCustomerPdf: () => void;
  onInternalPdf: () => void;
}

/** Keyboard shortcuts — verbatim port of source `document.addEventListener('keydown', ...)`
 *  (kos-qb-v3.js:5628–5683). Active only while the QB page is mounted. */
function wireShortcuts(h: ShortcutHandlers): () => void {
  const inField = (): boolean => {
    const t = document.activeElement;
    if (!t) return false;
    const tag = (t.tagName || "").toUpperCase();
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t as HTMLElement).isContentEditable;
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      const modal = document.querySelector(".qb-modal-overlay");
      if (modal) { e.preventDefault(); (modal as HTMLElement).remove(); return; }
      // Source line 5638: cancel active picker. Modular maps to clearing
      // the active picker selection.
      setActivePicker(null);
      return;
    }
    if (e.key === "?" && !inField()) {
      e.preventDefault();
      h.onShowShortcuts();
      return;
    }
    if (!(e.ctrlKey || e.metaKey)) return;
    switch (e.key.toLowerCase()) {
      case "s": e.preventDefault(); h.onSave(); break;
      case "m": e.preventDefault(); h.onMinimise(); break;
      case "b": e.preventDefault(); h.onToggleLeft(); break;
      case "n": e.preventDefault(); h.onOpenPicker(); break;
      case "d": e.preventDefault(); h.onDuplicateLine(); break;
      case "p":
        e.preventDefault();
        if (e.shiftKey) h.onInternalPdf();
        else h.onCustomerPdf();
        break;
    }
  };
  document.addEventListener("keydown", onKeyDown);
  return (): void => { document.removeEventListener("keydown", onKeyDown); };
}

/** Quotes archive modal — minimal port of source `_qbv3LoadQuoteModal`
 *  (kos-qb-v3.js ~5210). Lists previously saved quotes from the server,
 *  click to load. Server lookup uses /api/portal/quote-builder list. */
function openQuotesArchive(store: ReturnType<typeof createStore>, ctx: PageContext): void {
  if (document.querySelector("[data-role='qb-quotes-modal']")) return;
  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("data-role", "qb-quotes-modal");
  overlay.innerHTML = `
    <div class="qb-modal qb-modal--archive">
      <header class="qb-modal__head">
        <h3>Load a saved quote</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">×</button>
      </header>
      <div class="qb-modal__body" data-role="quotes-list">
        <div class="qb-modal__hint">Loading saved quotes…</div>
      </div>
    </div>
  `;
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", () => overlay.remove());
  document.body.appendChild(overlay);

  void (async (): Promise<void> => {
    try {
      const res = await fetch("/api/portal/quote-builder", {
        credentials: "include",
        headers: { "x-retailer-id": getCachedUser()?.retailerSlug || getCachedUser()?.retailerId || "" },
      });
      const data = await res.json();
      const list = Array.isArray(data.quotes) ? data.quotes : [];
      const body = overlay.querySelector<HTMLElement>("[data-role='quotes-list']");
      if (!body) return;
      if (list.length === 0) {
        body.innerHTML = `<div class="qb-modal__hint">No saved quotes yet.</div>`;
        return;
      }
      body.innerHTML = `
        <table class="qb-archive-table">
          <thead><tr><th>Reference</th><th>Customer</th><th>Status</th><th>Updated</th><th></th></tr></thead>
          <tbody>
            ${list.map((q: { id: string; reference: string; customer: string; status: string; updatedAt: number }) => `
              <tr>
                <td><code>${q.reference}</code></td>
                <td>${q.customer || "—"}</td>
                <td>${q.status}</td>
                <td>${q.updatedAt ? new Date(q.updatedAt).toLocaleString() : "—"}</td>
                <td><button type="button" class="qb-btn qb-btn--primary qb-btn--sm" data-load-id="${q.id}">Load</button></td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      `;
      body.querySelectorAll<HTMLButtonElement>("[data-load-id]").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const id = btn.dataset["loadId"];
          if (!id) return;
          try {
            const r = await fetch(`/api/portal/quote-builder/${encodeURIComponent(id)}`, {
              credentials: "include",
              headers: { "x-retailer-id": getCachedUser()?.retailerSlug || getCachedUser()?.retailerId || "" },
            });
            // Surface the real failure instead of silently doing nothing — a
            // 404 (quote not found) / 500 (server error) used to leave the modal
            // open with no feedback, which read as "won't load".
            if (!r.ok) {
              let detail = `HTTP ${r.status}`;
              try { const e = await r.json(); if (e && e.error) detail = `${e.error} (HTTP ${r.status})`; } catch { /* non-JSON body */ }
              console.error("[qb] quote load HTTP error", r.status, detail);
              ctx.showToast(`Couldn't load quote — ${detail}`, "error");
              return;
            }
            const j = await r.json();
            if (!j.ok || !j.quote) {
              console.error("[qb] quote load returned not-ok", j);
              ctx.showToast(j.error ? `Couldn't load quote — ${j.error}` : "Couldn't load quote — empty response", "error");
              return;
            }
            const q = j.quote;
            try {
              // Replace store state with the loaded quote.
              store.replace({
                version: 3,
                id: q.clientId || q.id,
                serverId: q.id,
                reference: q.reference,
                customer: q.customer,
                customerEmail: q.customerEmail,
                customerPhone: q.customerPhone,
                project: q.project,
                date: q.date,
                validDays: q.validDays,
                vatRate: q.vatRate,
                status: q.status,
                notes: q.notes,
                attachedDesigns: q.attachedDesigns ?? [],
                groupBy: q.groupBy,
                zones: q.zones ?? [],
                defaults: q.defaults ?? {},
                supplierDiscounts: q.supplierDiscounts ?? {},
                zoneSummary: q.zoneSummary ?? {},
                deliveryChargePence: q.deliveryChargePence ?? 0,
                termsAndConditions: q.termsAndConditions,
                openedAt: q.openedAt ?? 0,
                lastSavedAt: q.updatedAt || Date.now(),
                createdAt: q.createdAt || Date.now(),
              } as Parameters<typeof store.replace>[0]);
            } catch (hErr) {
              console.error("[qb] quote hydrate failed", hErr);
              ctx.showToast("Couldn't load quote — its saved format couldn't be read (details in console).", "error");
              return;
            }
            ctx.showToast(`Loaded ${q.reference}`, "success");
            overlay.remove();
          } catch (err) {
            console.error("[qb] quote load failed", err);
            ctx.showToast("Couldn't load quote — network error.", "error");
          }
        });
      });
    } catch {
      const body = overlay.querySelector<HTMLElement>("[data-role='quotes-list']");
      if (body) body.innerHTML = `<div class="qb-modal__hint">Could not load saved quotes.</div>`;
    }
  })();
}

/** Shortcuts cheatsheet modal — verbatim from source `_qbv3ShowShortcuts`
 *  (kos-qb-v3.js:5693+). */
function showShortcutsModal(): void {
  if (document.querySelector("[data-role='qb-shortcuts-modal']")) return;
  const rows: Array<[string, string]> = [
    ["Enter", "Confirm + add the current picker item"],
    ["Esc", "Close modal / cancel picker"],
    ["Tab / Shift+Tab", "Move between fields"],
    ["Ctrl+N", "New item (open picker)"],
    ["Ctrl+D", "Duplicate selected line"],
    ["Ctrl+S", "Save quote"],
    ["Ctrl+B", "Toggle left panel"],
    ["Ctrl+M", "Minimise (back to dashboard)"],
    ["Ctrl+P", "Customer PDF"],
    ["Ctrl+Shift+P", "Internal PDF"],
    ["?", "Show this cheatsheet"],
  ];
  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("data-role", "qb-shortcuts-modal");
  overlay.innerHTML = `
    <div class="qb-modal qb-modal--shortcuts">
      <header class="qb-modal__head">
        <h3>Keyboard shortcuts</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">×</button>
      </header>
      <div class="qb-modal__body">
        <table class="qb-shortcuts-table">
          ${rows.map(([k, d]) => `<tr><td><kbd>${k}</kbd></td><td>${d}</td></tr>`).join("")}
        </table>
      </div>
    </div>
  `;
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) overlay.remove();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", () => overlay.remove());
  document.body.appendChild(overlay);
}

/** Floating grand-total badge — verbatim port of source `renderBadge`
 *  (kos-qb-v3.js:1476–1482). Bottom-right, click → scroll to totals. */
function wireBadge(badgeEl: HTMLElement, store: ReturnType<typeof createStore>, root: HTMLElement): () => void {
  function paint(): void {
    const t = quoteTotals(store.get());
    if (t.lineCount === 0) {
      badgeEl.hidden = true;
      return;
    }
    badgeEl.hidden = false;
    badgeEl.innerHTML = `
      <span class="qb-badge__lbl">Total</span>
      <span class="qb-badge__amt">${fmtPence(t.grandTotalPence)}</span>
      <span class="qb-badge__lc">· ${t.lineCount} line${t.lineCount === 1 ? "" : "s"}</span>
    `;
  }
  const jumpToTotals = (): void => {
    const totalsCard = root.querySelector<HTMLElement>("[data-role='totals']");
    if (totalsCard) totalsCard.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  badgeEl.addEventListener("click", jumpToTotals);
  const unsub = store.subscribe(paint);
  paint();
  return (): void => {
    badgeEl.removeEventListener("click", jumpToTotals);
    unsub();
  };
}

registerPage(quoteBuilderPage);

export default quoteBuilderPage;
